import { useEffect, useState } from 'react'
import { Button, Card, CardBody, CardFooter, CardHeader, Select, SkeletonList, Switch } from '@/components/ui'
import { ConfirmDialog, QueryBoundary } from '@/components/domain'
import { useDisclosure } from '@/hooks'
import { useT } from '@/internationalization/i18n'
import { useNotificationSettings } from '../hooks'
import type { NotificationSettings as Notifications, SummaryRecipients } from '@/types'

const RECIPIENT_KEYS: { value: SummaryRecipients; key: string }[] = [
  { value: 'organizer-only', key: 'notifSettings.recipient.organizer' },
  { value: 'internal-invitees', key: 'notifSettings.recipient.internal' },
  { value: 'all-invitees', key: 'notifSettings.recipient.all' },
  { value: 'none', key: 'notifSettings.recipient.none' },
]

/** Ascending blast radius. Widening past this index needs a confirmation. */
const BLAST_RADIUS: SummaryRecipients[] = ['none', 'organizer-only', 'internal-invitees', 'all-invitees']

const AUDIENCE_KEY: Record<SummaryRecipients, string> = {
  'organizer-only': 'notifSettings.audience.organizer',
  'internal-invitees': 'notifSettings.audience.internal',
  'all-invitees': 'notifSettings.audience.all',
  none: 'notifSettings.audience.none',
}

/**
 * The highest-stakes control in the product.
 *
 * Every other stage of this pipeline is idempotent and re-runnable; an email is
 * not. So widening the recipient scope confirms first, and the confirmation
 * names the audience rather than asking "are you sure".
 */
export function NotificationSettings() {
  const t = useT()
  const { query, save } = useNotificationSettings()
  const [draft, setDraft] = useState<Notifications | null>(null)
  const [pending, setPending] = useState<SummaryRecipients | null>(null)
  const confirm = useDisclosure()

  useEffect(() => {
    if (query.data) setDraft(query.data)
  }, [query.data])

  const requestRecipientChange = (next: SummaryRecipients) => {
    if (!draft) return
    const isWidening = BLAST_RADIUS.indexOf(next) > BLAST_RADIUS.indexOf(draft.summaryRecipients)
    if (isWidening) {
      setPending(next)
      confirm.open()
      return
    }
    setDraft({ ...draft, summaryRecipients: next })
  }

  return (
    <QueryBoundary
      isLoading={query.isPending || !draft}
      error={query.error}
      onRetry={query.refetch}
      skeleton={<SkeletonList rows={4} className="sb-panel p-4" />}
    >
      {draft && (
        <div className="space-y-4">
          <Card>
            <CardHeader title={t('notifSettings.deliveryTitle')} />
            <CardBody className="space-y-4">
              <Select
                label={t('notifSettings.whoReceives')}
                value={draft.summaryRecipients}
                onChange={(event) => requestRecipientChange(event.target.value as SummaryRecipients)}
                options={RECIPIENT_KEYS.map((r) => ({ value: r.value, label: t(r.key) }))}
                hint={t('notifSettings.whoReceivesHint')}
              />
              <Select
                label={t('notifSettings.send')}
                value={draft.sendTiming}
                onChange={(event) =>
                  setDraft({ ...draft, sendTiming: event.target.value as Notifications['sendTiming'] })
                }
                options={[
                  { value: 'immediate', label: t('notifSettings.send.immediate') },
                  { value: 'end-of-day', label: t('notifSettings.send.endOfDay') },
                ]}
              />
            </CardBody>
            <CardFooter>
              <Button
                variant="primary"
                isLoading={save.isPending}
                onClick={() =>
                  save.mutate({
                    summaryRecipients: draft.summaryRecipients,
                    sendTiming: draft.sendTiming,
                  })
                }
              >
                {t('common.saveChanges')}
              </Button>
            </CardFooter>
          </Card>

          <Card>
            <CardHeader title={t('notifSettings.eventsTitle')} description={t('notifSettings.eventsDesc')} />
            <CardBody className="space-y-4">
              <Switch
                label={t('notifSettings.summaryEmail')}
                description={t('notifSettings.summaryEmailDesc')}
                showLabel
                checked={draft.summaryEmailEnabled}
                onChange={(summaryEmailEnabled) => {
                  setDraft({ ...draft, summaryEmailEnabled })
                  save.mutate({ summaryEmailEnabled })
                }}
              />
              <Switch
                label={t('notifSettings.failureAlerts')}
                description={t('notifSettings.failureAlertsDesc')}
                showLabel
                checked={draft.failureAlertsEnabled}
                onChange={(failureAlertsEnabled) => {
                  setDraft({ ...draft, failureAlertsEnabled })
                  save.mutate({ failureAlertsEnabled })
                }}
              />
              <Switch
                label={t('notifSettings.actionAssignment')}
                description={t('notifSettings.actionAssignmentDesc')}
                showLabel
                checked={draft.actionAssignedEnabled}
                onChange={(actionAssignedEnabled) => {
                  setDraft({ ...draft, actionAssignedEnabled })
                  save.mutate({ actionAssignedEnabled })
                }}
              />
              <Switch
                label={t('notifSettings.weeklyDigest')}
                description={t('notifSettings.weeklyDigestDesc')}
                showLabel
                checked={draft.weeklyDigestEnabled}
                onChange={(weeklyDigestEnabled) => {
                  setDraft({ ...draft, weeklyDigestEnabled })
                  save.mutate({ weeklyDigestEnabled })
                }}
              />
            </CardBody>
          </Card>

          <ConfirmDialog
            isOpen={confirm.isOpen}
            onCancel={() => {
              setPending(null)
              confirm.close()
            }}
            onConfirm={() => {
              if (pending) {
                setDraft({ ...draft, summaryRecipients: pending })
                save.mutate({ summaryRecipients: pending })
              }
              setPending(null)
              confirm.close()
            }}
            title={t('notifSettings.confirmTitle')}
            description={t('notifSettings.confirmBody', {
              audience: pending ? t(AUDIENCE_KEY[pending]) : '',
            })}
            confirmLabel={t('notifSettings.confirmButton')}
            isDestructive
          />
        </div>
      )}
    </QueryBoundary>
  )
}
